import {
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import moment, { type Moment } from 'moment'
import React, { useCallback, useEffect, useRef } from 'react'
import ChevronDown from '../../../components/icons/chevron-down'
import ChevronLeft from '../../../components/icons/chevron-left'
import ChevronRight from '../../../components/icons/chevron-right'

interface MiniCalendarProps {
  onDateClick: (date: Moment) => void
  selectedDate: Moment
}

export const MiniCalendar: React.FC<MiniCalendarProps> = ({
  onDateClick,
  selectedDate
}) => {
  const calendarRef = useRef<FullCalendar>(null)

  const [viewDate, setViewDate] = React.useState<Moment>(selectedDate)

  const dayHeaderContent = (arg: any) => (
    <Text color="#757575" fontSize="10px">
      {arg.text.charAt(0).toUpperCase()}
    </Text>
  )

  const handleDateClick = useCallback(
    (info: DateClickArg) => {
      onDateClick(moment(info.date))
    },
    [onDateClick]
  )

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi()
    if (calendarApi) {
      calendarApi.gotoDate(selectedDate.toDate())
      setViewDate(moment(calendarApi.getDate()))
    }
  }, [selectedDate])

  const downIcon = (
    <ChevronDown
      width={14}
      height={14}
      style={{ position: 'relative', top: '-2px' }}
    />
  )

  return (
    <Box>
      <Flex align="center" mb="-6" mt="2" gap="1">
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            variant="ghost"
            fontSize="12px"
            fontWeight="bold"
            lineHeight="16px"
            letterSpacing="0px"
            color="#282828"
            leftIcon={downIcon}
            _hover={{ bg: 'rgba(45,84,232,0.04)' }}
            fontFamily="TofinoPersonal"
          >
            {viewDate.format('MMM YYYY')}
          </MenuButton>
          <MenuList zIndex={9999} maxHeight="300px" overflowY="auto">
            {Array.from({ length: 12 }).map((_, idx) => {
              const year = viewDate.year()
              const monthDate = moment().month(idx).year(year)
              const label = monthDate.format('MMM')
              const monthKey = monthDate.format('YYYY-MM')
              return (
                <MenuItem
                  key={monthKey}
                  onClick={() => {
                    const newDate = moment(viewDate).month(idx).date(1)
                    calendarRef.current?.getApi().gotoDate(newDate.toDate())
                    setViewDate(newDate)
                  }}
                >
                  {label}
                </MenuItem>
              )
            })}
          </MenuList>
        </Menu>

        <HStack spacing="1.5" ml="-3" alignItems="center">
          <Button
            onClick={() => {
              const api = calendarRef.current?.getApi()
              api?.prev()
              if (api) setViewDate(moment(api.getDate()))
            }}
            size="sm"
            variant="ghost"
            fontWeight="400"
            fontSize="16px"
            padding="0 4px"
            minW="24px"
            height="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: 'rgba(45,84,232,0.04)' }}
          >
            <ChevronLeft
              width={12}
              height={12}
              style={{ position: 'relative', top: '-1px' }}
            />
          </Button>

          <Button
            onClick={() => {
              const api = calendarRef.current?.getApi()
              api?.next()
              if (api) setViewDate(moment(api.getDate()))
            }}
            size="sm"
            variant="ghost"
            fontWeight="400"
            fontSize="16px"
            padding="0 4px"
            minW="24px"
            height="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: 'rgba(45,84,232,0.04)' }}
          >
            <ChevronRight
              width={12}
              height={12}
              style={{ position: 'relative', top: '-1px' }}
            />
          </Button>
        </HStack>
      </Flex>

      <Box
        borderRadius="12px"
        overflow="hidden"
        sx={{
          '--fc-border-color': 'transparent',
          '--fc-button-bg-color': 'transparent',
          '--fc-button-hover-bg-color': 'transparent',
          '--fc-button-active-bg-color': 'transparent',
          '--fc-button-active-border-color': 'transparent',
          '--fc-button-border-color': 'transparent',
          '--fc-button-text-color': '#1B1B28',
          '&': {
            '.fc-daygrid-day-events': {
              minHeight: 'auto',
              margin: '0px'
            },
            '.fc-col-header': {
              backgroundColor: '#F9F9F9'
            },
            '.fc-day': {
              fontSize: '13px',
              fontWeight: '400',
              padding: '1px',
              color: '#1B1B28',
              textAlign: 'center',
              '.fc-daygrid-day-top': {
                justifyContent: 'center',
                display: 'block',
                lineHeight: '26px'
              },
              '&.fc-day-other': {
                color: '#C9C9C9',
                '.fc-daygrid-day-top': {
                  opacity: 1
                }
              },
              [`&[data-date="${selectedDate.format(
                'YYYY-MM-DD'
              )}"] .fc-daygrid-day-top`]: {
                backgroundColor: '#C0CCF8',
                borderRadius: '50%',
                color: '#1B1B28',
                fontSize: '13px',
                fontWeight: '400',
                minWidth: '26px',
                height: '26px',
                lineHeight: '26px',
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              },
              '.fc-daygrid-day-top:hover': {
                backgroundColor: '#E0E0E0',
                borderRadius: '50%',
                minWidth: '26px',
                height: '26px',
                lineHeight: '26px',
                color: '#1B1B28',
                margin: '0 auto',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              },
              '&.fc-day-today': {
                backgroundColor: 'transparent',
                '.fc-daygrid-day-top': {
                  backgroundColor: '#2D54E8',
                  borderRadius: '50%',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: '700',
                  minWidth: '26px',
                  height: '26px',
                  lineHeight: '26px',
                  margin: '0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }
            },
            '.fc-daygrid-day-number': {
              fontSize: '10px',
              fontWeight: '430',
              fontStyle: 'normal',
              lineHeight: '145%',
              letterSpacing: '0px',
              color: '#282828',
              textAlign: 'center',
              textDecoration: 'none',
              fontFamily: 'TofinoPersonal'
            },
            '.fc-day-today .fc-daygrid-day-number': {
              color: '#FFFFFF',
              fontWeight: '430',
              fontFamily: 'TofinoPersonal'
            },
            '.fc-day-other .fc-daygrid-day-number': {
              color: '#C9C9C9',
              opacity: 1,
              fontFamily: 'TofinoPersonal'
            },
            '.fc-scrollgrid-sync-table': {
              tableLayout: 'fixed',
              width: '100%'
            },
            '.fc-daygrid-day-frame': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0',
              height: '28px'
            },
            '.fc-daygrid-body': {
              marginTop: '0px'
            },
            '.fc-col-header-cell-cushion': {
              fontSize: '10px',
              fontWeight: '400',
              color: '#757575'
            },
            '.fc-scrollgrid': {
              margin: '0',
              padding: '0'
            },
            '.fc-scrollgrid-section-header': {
              marginBottom: '0',
              paddingBottom: '0'
            },
            '.fc .fc-scrollgrid-sync-table': {
              marginTop: '0',
              paddingTop: '0'
            },
            '.fc-view-harness': {
              paddingTop: '0',
              marginTop: '0'
            },
            '.fc .fc-daygrid': {
              marginTop: '0'
            }
          }
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          dayHeaderContent={dayHeaderContent}
          firstDay={0}
          height="auto"
          headerToolbar={{ start: '', center: '', end: '' }}
          dateClick={handleDateClick}
        />
      </Box>
    </Box>
  )
}
